"use client";

import { closeToast, type Toast as HeroToast, addToast as heroAddToast } from "@heroui/toast";
import { cn } from "../lib/utils";

type HeroToastColor = React.ComponentProps<typeof HeroToast>["color"];
type ExtendedColor = HeroToastColor | "pop" | "punch" | "soft";

interface ToastClassNames {
  base?: string;
  content?: string;
  wrapper?: string;
  foreground?: string;
  icon?: string;
  title?: string;
  description?: string;
  closeButton?: string;
  closeIcon?: string;
  motionDiv?: string;
}

interface ExtendedToastOptions {
  color?: ExtendedColor;
  classNames?: ToastClassNames;
  title?: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  endContent?: React.ReactNode;
  promise?: Promise<unknown>;
  timeout?: number;
  [key: string]: unknown;
}

// Color variant class mappings using Tailwind semantic color classes
const colorClassNames = {
  default: {
    base: "bg-default-100",
    foreground: "text-foreground",
    icon: "text-default-600",
    title: "text-foreground",
    description: "text-default-600",
    closeButton: "text-default-600 border-default-600 bg-default-100",
  },
  primary: {
    base: "bg-primary-100",
    foreground: "text-foreground",
    icon: "text-primary-600",
    title: "text-foreground",
    description: "text-default-600",
    closeButton: "text-default-600 border-default-600 bg-primary-100",
  },
  secondary: {
    base: "bg-secondary",
    foreground: "text-secondary-foreground",
    icon: "text-secondary-foreground",
    title: "text-secondary-foreground",
    description: "text-secondary-foreground/70",
    closeButton: "text-secondary-foreground border-secondary-foreground bg-secondary",
  },
  soft: {
    base: "bg-purple-100",
    foreground: "text-foreground",
    icon: "text-purple-500",
    title: "text-foreground dark:text-secondary-foreground",
    description: "text-default-600 dark:text-secondary-foreground/70",
    closeButton: "text-default-600 border-default-600 bg-purple-100",
  },
  pop: {
    base: "bg-chartreuse-100",
    foreground: "text-foreground",
    icon: "text-chartreuse-800",
    title: "text-foreground dark:text-secondary-foreground",
    description: "text-default-600 dark:text-secondary-foreground/70",
    closeButton: "text-default-600 border-default-600 bg-chartreuse-100",
  },
  punch: {
    base: "bg-orange-100",
    foreground: "text-foreground",
    icon: "text-orange-600",
    title: "text-foreground dark:text-secondary-foreground",
    description: "text-default-600 dark:text-secondary-foreground/70",
    closeButton: "text-default-600 border-default-600 bg-orange-100",
  },
  success: {
    base: "bg-success-100",
    foreground: "text-foreground",
    icon: "text-success-700",
    title: "text-foreground",
    description: "text-default-600",
    closeButton: "text-default-600 border-default-600 bg-success-100",
  },
  warning: {
    base: "bg-warning-100",
    foreground: "text-foreground",
    icon: "text-warning-700",
    title: "text-foreground",
    description: "text-default-600",
    closeButton: "text-default-600 border-default-600 bg-warning-100",
  },
  danger: {
    base: "bg-danger-100",
    foreground: "text-foreground",
    icon: "text-danger-600",
    title: "text-foreground",
    description: "text-default-600",
    closeButton: "text-default-600 border-default-600 bg-danger-100",
  },
} as const;

export const addToast = (options: ExtendedToastOptions) => {
  const { color = "default", classNames = {}, promise, timeout = 5000, ...rest } = options;

  // Map extended colors to valid HeroToast colors
  const heroToastColor = (color === "soft" || color === "pop" || color === "punch" ? "default" : color) as
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger";

  // Get color-specific classNames
  const variantClassNames = colorClassNames[color as keyof typeof colorClassNames];

  // Merge variant classNames with user-provided classNames using cn
  const mergedClassNames: ToastClassNames = {
    base: cn(variantClassNames.base, "max-sm:mx-auto max-sm:w-[calc(100vw-2rem)]", classNames.base),
    content: cn(classNames.content),
    wrapper: cn(classNames.wrapper),
    foreground: cn(variantClassNames.foreground, classNames.foreground),
    icon: cn(variantClassNames.icon, classNames.icon),
    title: cn("wrap-anywhere", variantClassNames.title, classNames.title),
    description: cn("wrap-anywhere", variantClassNames.description, classNames.description),
    closeButton: cn("opacity-100", variantClassNames.closeButton, classNames.closeButton),
    closeIcon: cn(classNames.closeIcon),
    motionDiv: cn("max-sm:box-border", classNames.motionDiv),
  };

  const toastKey = heroAddToast({
    ...rest,
    color: heroToastColor,
    timeout,
    classNames: mergedClassNames,
  });

  if (
    typeof window !== "undefined" &&
    toastKey &&
    !promise &&
    typeof timeout === "number" &&
    timeout > 0 &&
    window.matchMedia?.("(max-width: 640px)")?.matches
  ) {
    window.setTimeout(() => closeToast(toastKey), timeout);
  }

  return toastKey;
};

export { closeToast };
